import React, { useState, useEffect } from "react";
import { getDashboardStats, getWaveformTechSummary } from "../lib/api";
import { useTechIdentity } from "../lib/TechIdentityContext";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";

const RANGES = [
  { key: "1m",  label: "Last month" },
  { key: "3m",  label: "Last 3 months" },
  { key: "6m",  label: "Last 6 months" },
  { key: "1y",  label: "Last year" },
  { key: "all", label: "All time" },
];

const TIERS = [
  { id: "above",    label: "Above Average", min: 95, range: "95+",   color: "#1D9E75" },
  { id: "average",  label: "Average",       min: 90, range: "90-94", color: "#185FA5" },
  { id: "below",    label: "Below Average", min: 80, range: "80-89", color: "#854F0B" },
  { id: "critical", label: "Critical",      min: 0,  range: "<80",   color: "#A32D2D" },
];
const tierFor = (s) => s == null
  ? { label: "No data", color: "var(--text-tertiary)" }
  : TIERS.find(t => s >= t.min) || TIERS[TIERS.length - 1];

function Card({ label, value, sub, color }) {
  return (
    <div style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:8, padding:"14px 16px" }}>
      <div style={{ fontSize:26, fontWeight:300, color:color||"var(--text-primary)", lineHeight:1, letterSpacing:"-0.02em" }}>{value}</div>
      <div style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginTop:4 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:"var(--text-tertiary)", marginTop:2 }}>{sub}</div>}
    </div>
  );
}

const MONTHS_BACK = { "1m": 1, "3m": 3, "6m": 6, "1y": 12 };

function rangeSubtitle(key) {
  if (key === "all") return "All cases to date";
  const back = MONTHS_BACK[key];
  if (!back) return "";
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - back, now.getDate());
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return `${fmt(from)} — ${fmt(now)}`;
}

function rangeDates(key) {
  if (key === "all") return { from: "", to: "" };
  const back = MONTHS_BACK[key];
  if (!back) return { from: "", to: "" };
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - back, now.getDate());
  const iso = (d) => d.toISOString().split("T")[0];
  return { from: iso(from), to: iso(now) };
}

export default function TechDashboard() {
  // effectiveWebId, not techWebId: this is the page an admin sees while
  // viewing as a tech, so it has to follow the impersonated identity.
  const { effectiveWebId: techWebId, loading: identityLoading } = useTechIdentity();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceRange, setServiceRange] = useState("3m");
  const [waveform, setWaveform] = useState(null);
  const [waveLoading, setWaveLoading] = useState(true);

  useEffect(() => {
    if (identityLoading || !techWebId) { setWaveLoading(false); return; }
    const { from, to } = rangeDates(serviceRange);
    setWaveLoading(true);
    getWaveformTechSummary("", from, to, "", techWebId)
      .then(r => setWaveform(Object.values(r?.techs || {})[0] || null))
      .catch(() => setWaveform(null))
      .finally(() => setWaveLoading(false));
  }, [techWebId, identityLoading, serviceRange]);

  useEffect(() => {
    if (identityLoading) return;
    if (!techWebId) { setLoading(false); return; }
    setLoading(true);
    getDashboardStats(serviceRange, "3m", techWebId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [techWebId, identityLoading, serviceRange]);

  const s = data?.stats || {};
  const pct = (v) => (v == null ? "—" : `${v}%`);
  const modalities = Object.entries(waveform || {})
    .map(([name, v]) => ({ name, score: v.score, caseCount: v.case_count }))
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1) || b.caseCount - a.caseCount);

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">My QA Performance</div>
          <div className="page-subtitle">{rangeSubtitle(serviceRange)}</div>
        </div>
      </div>

      <div className="page-body" style={{ maxWidth:920 }}>
        {!identityLoading && techWebId && (
          <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
            {RANGES.map(r => (
              <button key={r.key} onClick={() => setServiceRange(r.key)} disabled={loading}
                style={{ padding:"4px 12px", borderRadius:20, fontSize:12, fontFamily:"var(--font-mono)",
                  cursor:loading ? "default" : "pointer", opacity:loading ? 0.6 : 1,
                  border:`1px solid ${serviceRange===r.key ? "var(--blue-600, #2563eb)" : "var(--border-strong)"}`,
                  background:serviceRange===r.key ? "var(--blue-50, #eff6ff)" : "var(--white)",
                  color:serviceRange===r.key ? "var(--blue-600, #2563eb)" : "var(--text-secondary)",
                  fontWeight:serviceRange===r.key ? 600 : 400 }}>{r.label}</button>
            ))}
          </div>
        )}

        {(loading || identityLoading) ? (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
            {[0,1,2,3].map(i => (
              <div key={i} style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:8, padding:"14px 16px" }}>
                <div style={{ height:26, width:"55%", background:"var(--gray-100)", borderRadius:4 }}/>
                <div style={{ height:10, width:"75%", background:"var(--gray-100)", borderRadius:3, marginTop:8 }}/>
                <div style={{ height:10, width:"40%", background:"var(--gray-100)", borderRadius:3, marginTop:5 }}/>
              </div>
            ))}
          </div>
        ) : !techWebId ? (
          <div className="card"><div style={{ padding:"48px 24px", textAlign:"center" }}>
            <div style={{ fontSize:14, fontWeight:600, color:"var(--text-secondary)", marginBottom:6 }}>No performance data</div>
            <div style={{ fontSize:13, color:"var(--text-tertiary)" }}>Your account isn't linked to a technologist record. Contact an administrator.</div>
          </div></div>
        ) : (
          <> 
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18 }}>
              <Card label="Overall Score" value={pct(s.overallScore)} color={tierFor(s.overallScore).color}
                sub={tierFor(s.overallScore).label} />
              <Card label="Documentation" value={pct(s.documentationScore)} color={tierFor(s.documentationScore).color}
                sub={`${s.totalDocuments || 0} case${s.totalDocuments !== 1 ? "s" : ""}`} />
              <Card label="Waveform Analysis" value={pct(s.waveformScore)} color={tierFor(s.waveformScore).color}
                sub={s.waveformScore == null ? "No waveform QA reviewed" : "Reviewed this period"} />
              {(() => {
                // Change from the previous period of the same length. No rank:
                // the client removed rankings from the tech views (9/21/2026).
                const d = s.prevOverallScore == null || s.overallScore == null ? null : s.overallScore - s.prevOverallScore;
                return (
                  <Card label="vs Previous Period"
                    value={d == null ? "—" : d === 0 ? "No change" : <span><span style={{ fontSize: "0.45em", verticalAlign: "middle", marginRight: 5, position: "relative", top: "-0.1em" }}>{d > 0 ? "▲" : "▼"}</span>{Math.abs(d)} pts</span>}
                    color={d == null || d === 0 ? "var(--text-secondary)" : d > 0 ? "#1D9E75" : "#A32D2D"}
                    sub={serviceRange === "all" ? "No earlier period" : s.prevOverallScore == null ? "No cases in the previous period" : `from ${s.prevOverallScore}%`} />
                );
              })()}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                {(data?.trend || []).filter(t => t.score != null).length > 1 && (
                <div className="card">
                    <div className="card-header">
                    <div>
                        <div className="card-title">Score Over Time</div>
                        <div className="card-subtitle">Your pooled score per period</div>
                    </div>
                    </div>
                    <div style={{ padding:"10px 12px 4px" }}>
                    <ResponsiveContainer width="100%" height={190}>
                        <LineChart data={(data.trend || []).filter(t => t.score != null)} margin={{ top:4, right:8, bottom:0, left:-24 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" vertical={false}/>
                        <XAxis dataKey="date" tick={{ fill:"var(--text-tertiary)", fontSize:10, fontFamily:"var(--font-mono)" }} axisLine={false} tickLine={false}/>
                        <YAxis domain={[dMin => Math.min(80, Math.floor(dMin)), 100]} tick={{ fill:"var(--text-tertiary)", fontSize:10, fontFamily:"var(--font-mono)" }} axisLine={false} tickLine={false}/>
                        <Tooltip formatter={(v) => [`${v}%`, "Score"]}/>
                        {TIERS.filter(t => t.min > 0).map(t => (
                            <ReferenceLine key={t.id} y={t.min} stroke={t.color} strokeDasharray="3 3" strokeOpacity={0.5}/>
                        ))}
                        <Line type="monotone" dataKey="score" stroke="#1d4ed8" strokeWidth={2} dot={{ r:3.5, fill:"#1d4ed8" }}/>
                        </LineChart>
                    </ResponsiveContainer>
                    </div>
                </div>
                )}
                {(data?.categoryScores || []).length > 0 && (
                <div className="card">
                    <div className="card-header">
                    <div>
                        <div className="card-title">Performance by Category</div>
                        <div className="card-subtitle">Pass rate across QA categories</div>
                    </div>
                    </div>
                    <div style={{ padding:"10px 12px 4px" }}>
                    <ResponsiveContainer width="100%" height={Math.max(140, data.categoryScores.length * 34)}>
                        <BarChart data={data.categoryScores} layout="vertical" margin={{ top:4, right:16, bottom:0, left:4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" horizontal={false}/>
                        <XAxis type="number" domain={[0,100]} tick={{ fill:"var(--text-tertiary)", fontSize:10, fontFamily:"var(--font-mono)" }} axisLine={false} tickLine={false}/>
                        <YAxis type="category" dataKey="name" width={110} tick={{ fill:"var(--text-secondary)", fontSize:11 }} axisLine={false} tickLine={false}/>
                        <Tooltip formatter={(v) => [`${v}%`, "Pass rate"]}/>
                        <Bar dataKey="avg" radius={[0,3,3,0]} barSize={12}>
                            {data.categoryScores.map((c, i) => <Cell key={i} fill={tierFor(c.avg).color}/>)}
                        </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    </div>
                </div>
                )}
            </div>

            {(data?.topFailedChecks || []).length > 0 && (
              <div className="card" style={{ marginBottom:14 }}>
                <div className="card-header">
                  <div>
                    <div className="card-title">Most Frequently Missed Checks</div>
                    <div className="card-subtitle">Where your cases lose the most points</div>
                  </div>
                </div>
                <div style={{ padding:"4px 0" }}>
                  {data.topFailedChecks.map(c => (
                    <div key={c.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 16px", borderBottom:"1px solid var(--border)" }}>
                      <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0, minWidth:40 }}>{c.id}</span>
                      <span style={{ flex:1, fontSize:12, color:"var(--text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</span>
                      <span style={{ fontSize:11.5, fontFamily:"var(--font-mono)", fontWeight:600, color:"var(--error)", flexShrink:0 }}>{c.count} miss{c.count !== 1 ? "es" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {modalities.length > 0 && (
              <div className="card" style={{ marginBottom:14 }}>
                <div className="card-header">
                  <div>
                    <div className="card-title">Modality Performance</div>
                    <div className="card-subtitle">Waveform QA by modality, sized by case volume</div>
                  </div>
                </div>
                <div style={{ padding:"12px 16px" }}>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                    {modalities.map(m => {
                      const t = tierFor(m.score);
                      const total = modalities.reduce((a, x) => a + x.caseCount, 0) || 1;
                      const w = Math.max(14, Math.round((m.caseCount / total) * 100));
                      return (
                        <div key={m.name} style={{ width:`${w}%`, minWidth:86, boxSizing:"border-box",
                          padding:"8px 10px", border:`1px solid ${t.color}40`, background:`${t.color}14`, borderRadius:5 }}>
                          <div style={{ fontSize:10.5, fontWeight:700, color:t.color }}>{m.name}</div>
                          <div style={{ fontSize:19, fontWeight:300, color:t.color, lineHeight:1.2 }}>
                            {m.score == null ? "—" : `${m.score}%`}
                          </div>
                          <div style={{ fontSize:9, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)" }}>
                            {m.caseCount} case{m.caseCount !== 1 ? "s" : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display:"flex", gap:14, flexWrap:"wrap", alignItems:"center" }}>
              {TIERS.map(t => (
                <span key={t.id} style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:10.5, color:"var(--text-tertiary)" }}>
                  <span style={{ width:9, height:9, borderRadius:2, background:t.color, display:"inline-block" }}/>
                  {t.label} {t.range}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}